/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { enterInfluencer } from '../utils/influencerContext'
import { useQuery, useMutation, gql } from '@apollo/client'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import InfluencerComponent from '../components/Influencer/Influencer'
import SearchBar from '../components/TableHeader/SearchBar'
import {
  getInfluencers,
  deleteInfluencer,
  toggleInfluencerEnabled
} from '../apollo'
import useGlobalStyles from '../utils/globalStyles'
import { Avatar, Box, Container, Modal, Switch, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { customStyles } from '../utils/tableCustomStyles'
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import { RowActionMenu, ConfirmDialog } from '../components/UI/RowActionMenu'
import TableHeader from '../components/TableHeader'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_INFLUENCERS = gql`
  ${getInfluencers}
`
const DELETE_INFLUENCER = gql`
  ${deleteInfluencer}
`
const TOGGLE_INFLUENCER = gql`
  ${toggleInfluencerEnabled}
`

function Influencers(props) {
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [influencer, setInfluencer] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const history = useHistory()
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Same move as clicking a restaurant: remember who, then step into their
  // own dashboard layout
  const openDashboard = row => {
    enterInfluencer(row)
    history.push('/influencer/home')
  }

  const [mutateToggle] = useMutation(TOGGLE_INFLUENCER, {
    refetchQueries: [{ query: GET_INFLUENCERS }]
  })
  const [mutateDelete, { loading: deleting }] = useMutation(DELETE_INFLUENCER, {
    refetchQueries: [{ query: GET_INFLUENCERS }],
    onCompleted: () => setDeleteTarget(null),
    onError: () => setDeleteTarget(null)
  })
  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_INFLUENCERS
  )

  const toggleModal = influencer => {
    setEditModal(!editModal)
    setInfluencer(influencer)
  }

  const closeEditModal = () => {
    setEditModal(false)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (typeof row[field] === 'string') {
        return row[field].toLowerCase()
      }
      return row[field]
    }
    return orderBy(rows, handleField, direction)
  }

  const handleSort = (column, sortDirection) =>
    console.log(column.selector, sortDirection)

  const { t } = props

  const columns = [
    {
      name: t('Name'),
      sortable: true,
      selector: 'name',
      minWidth: '180px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            src={row.image || undefined}
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 700,
              bgcolor: 'rgba(244,102,31,0.12)',
              color: 'primary.main'
            }}>
            {(row.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {row.name}
          </Box>
        </Box>
      )
    },
    {
      name: t('Email'),
      sortable: true,
      selector: 'email'
    },
    {
      name: t('Password'),
      sortable: true,
      selector: 'password'
    },
    {
      name: t('Phone'),
      sortable: true,
      selector: 'phone'
    },
    {
      name: t('Coupons'),
      cell: row => (
        <>
          {row.coupons && row.coupons.length
            ? row.coupons.map(coupon => coupon.title).join(', ')
            : '-'}
        </>
      )
    },
    {
      name: t('RatePerOrder'),
      sortable: true,
      selector: 'ratePerOrder',
      cell: row => <>{`£${Number(row.ratePerOrder || 0).toFixed(2)}`}</>
    },
    {
      name: t('Orders'),
      sortable: true,
      selector: 'ordersCount'
    },
    {
      name: t('TotalEarnings'),
      sortable: true,
      selector: 'totalEarnings',
      cell: row => <>{`£${Number(row.totalEarnings || 0).toFixed(2)}`}</>
    },
    {
      name: t('Enabled'),
      cell: row => <>{enabledStatus(row)}</>
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]

  const enabledStatus = row => {
    return (
      <Box onClick={e => e.stopPropagation()}>
        <Switch
          size="small"
          checked={row.enabled}
          onChange={_event => {
            mutateToggle({ variables: { id: row._id } })
          }}
        />
      </Box>
    )
  }

  const actionButtons = row => (
    <RowActionMenu
      label={t('Action')}
      actions={[
        {
          label: t('ViewDashboard'),
          icon: SpaceDashboardOutlined,
          tone: 'blue',
          onClick: () => openDashboard(row)
        },
        {
          label: t('Edit'),
          icon: EditOutlined,
          tone: 'primary',
          onClick: () => toggleModal(row)
        },
        {
          label: t('Delete'),
          icon: DeleteOutlineRounded,
          tone: 'danger',
          confirm: false,
          divider: true,
          onClick: () => setDeleteTarget(row)
        }
      ]}
    />
  )

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.influencers
      : data &&
        data.influencers.filter(influencer => {
          return (
            (influencer.name || '').toLowerCase().search(regex) > -1 ||
            (influencer.email || '').toLowerCase().search(regex) > -1 ||
            (influencer.phone || '').toLowerCase().search(regex) > -1 ||
            (influencer.coupons || [])
              .map(coupon => coupon.title)
              .join(' ')
              .toLowerCase()
              .search(regex) > -1
          )
        })

  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Influencer')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {/* Table */}
        {errorQuery ? (
          <tr>
            <td>`Error! ${errorQuery.message}`</td>
          </tr>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <DataTable
              subHeader={true}
              subHeaderComponent={
                <SearchBar
                  value={searchQuery}
                  onChange={onChangeSearch}
                  onClick={() => refetch()}
                />
              }
              title={<TableHeader title={t('Influencers')} />}
              columns={columns}
              data={filtered}
              pagination
              progressPending={loadingQuery}
              progressComponent={<CustomLoader />}
              onSort={handleSort}
              sortFunction={customSort}
              customStyles={customStyles}
              pointerOnHover
              highlightOnHover
              onRowClicked={openDashboard}
            />
          </Box>
        )}
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal()
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <InfluencerComponent
              influencer={influencer}
              onClose={closeEditModal}
            />
          </Box>
        </Modal>
        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={`${t('Delete')} ${deleteTarget?.name || t('Influencer')}?`}
          message={t('DeleteInfluencerConfirm')}
          confirmLabel={t('Delete')}
          cancelLabel={t('Cancel')}
          loading={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() =>
            mutateDelete({ variables: { id: deleteTarget._id } })
          }
        />
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <InfluencerComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Influencers)
